import React, { useState } from 'react';
import { PlugZap, RefreshCw, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  buildCoexistenceNotice,
  type CoexistenceState,
} from '@/lib/whatsapp/coexistenceState';

interface CoexistenceStateBannerProps {
  coexistenceState?: CoexistenceState | string | null;
  coexistenceReason?: string | null;
  accountName?: string | null;
  phoneNumber?: string | null;
  onDismiss?: () => void;
  /** "‹ 1/2 ›" controls when shown inside the top-of-app banner carousel. */
  carouselControls?: React.ReactNode;
}

const TONE_CLASSES = {
  info: {
    shell: 'border-[#B2DDFF] bg-[#EFF8FF]',
    chip: 'bg-[#D1E9FF] text-[#175CD3]',
    headline: 'text-[#194185]',
    body: 'text-[#175CD3]',
    dismiss: 'text-[#175CD3] hover:bg-[#D1E9FF]',
  },
  error: {
    shell: 'border-[#FECDCA] bg-[#FEF3F2]',
    chip: 'bg-[#FEE4E2] text-[#B42318]',
    headline: 'text-[#912018]',
    body: 'text-[#B42318]',
    dismiss: 'text-[#B42318] hover:bg-[#FEE4E2]',
  },
} as const;

export const CoexistenceStateBanner: React.FC<CoexistenceStateBannerProps> = ({
  coexistenceState,
  coexistenceReason,
  accountName,
  phoneNumber,
  onDismiss,
  carouselControls,
}) => {
  const [dismissed, setDismissed] = useState(false);
  const navigate = useNavigate();

  const notice = buildCoexistenceNotice({
    state: coexistenceState,
    reason: coexistenceReason,
    accountName,
    phoneNumber,
  });

  if (!notice) return null;
  if (dismissed) return null;

  const tone = TONE_CLASSES[notice.tone];
  const Icon = notice.tone === 'info' ? RefreshCw : PlugZap;

  return (
    <div
      className={`z-50 flex w-full items-start justify-between gap-3 border-b px-4 py-2.5 ${tone.shell}`}
      data-testid="coexistence-state-banner"
      data-state={notice.state}
      role="status"
    >
      <div className="flex min-w-0 items-start gap-2.5">
        <span
          className={`mt-px inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${tone.chip}`}
        >
          <Icon className="h-3.5 w-3.5" strokeWidth={2.25} aria-hidden />
        </span>
        <div className="min-w-0">
          <p
            className={`truncate text-[13px] font-semibold leading-5 ${tone.headline}`}
            data-testid="coexistence-state-banner-headline"
          >
            {notice.headline}
          </p>
          <p
            className={`line-clamp-2 h-8 text-[11px] font-medium leading-4 ${tone.body}`}
            data-testid="coexistence-state-banner-guidance"
          >
            {notice.guidance}
          </p>
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
        {notice.reconnect && (
          <button
            type="button"
            onClick={() => navigate(notice.reconnect!.to)}
            data-testid="coexistence-state-banner-reconnect-cta"
            className="inline-flex h-8 items-center rounded-lg bg-[#B42318] px-3 text-[12px] font-semibold text-white transition hover:bg-[#912018] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F04438]/40"
          >
            {notice.reconnect.label}
          </button>
        )}
        {/* A disconnected number stays on screen until it is reconnected —
            every send is failing, so the user must not be able to hide it.
            The transient "restoring" notice can still be dismissed. */}
        {carouselControls}
        {notice.tone !== 'error' && (
          <button
            type="button"
            onClick={() => {
              setDismissed(true);
              onDismiss?.();
            }}
            className={`inline-flex h-8 w-8 items-center justify-center rounded-lg transition ${tone.dismiss}`}
            aria-label="Dismiss banner"
            data-testid="coexistence-state-banner-dismiss"
          >
            <X className="h-4 w-4" strokeWidth={2.25} />
          </button>
        )}
      </div>
    </div>
  );
};
